import { useEffect, useMemo, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import DialogContent from '@zenbusiness/zui/DialogContent';
import FormControl from '@zenbusiness/zui/FormControl';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { EIN_INVALID_MESSAGE } from '#client/components/OpenAccountApplication/components/CustomFields/constants/errorMessages';
import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { MODAL_NAMES } from '#client/constants/modals';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useToastContext from '#client/hooks/useToastContext';
import { PATCH_APPLICATION_INIT_DATA_MUTATION } from '#client/mutations';

import Skeleton from '../../Skeleton';
import { WrapperContentStyled } from '../EnterEINNumberModal.styles';

const ALLOW_ENTER_EIN_STATUS_LIST = [
  DB_APPLICATION_STATUS.AWAITING_EIN,
  DB_APPLICATION_STATUS.AWAITING_EIN_ORDER_CREATION
];

function EnterEINNumber({ handleClose }) {
  const [values, setValues] = useState({
    ein: '',
    valid: true,
    isDirty: false,
    isFocus: false
  });
  const [patchApplicationData, { loading: patchApplicationLoading }] = useMutation(
    PATCH_APPLICATION_INIT_DATA_MUTATION,
    {
      noThrow: true
    }
  );
  const { createCommonErrorToast, createSuccessToast, clearAllToast } = useToastContext();
  const { isOpeningModal } = useCheckOpenModal();
  const navigate = useNavigate();
  const submitButtonId = useEnterSubmit();
  const {
    appStatus,
    applicationId,
    loading: applicationStatusLoading,
    appStatusError
  } = useApplicationStatusContext();
  const { businessEntity, loading, error: businessEntityError } = useBusinessEntity();

  useEffect(() => {
    if (businessEntityError) {
      createCommonErrorToast({ error: businessEntityError });
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessEntityError]);

  const isShowError = values.isDirty && !values.valid && !values.isFocus;

  const handleEinChange = (event) => {
    const newValue = event.target.value;
    const newValues = {
      ...values,
      valid: /^\d{9}$/.test(newValue),
      ein: newValue
    };
    setValues(newValues);
  };

  const inputNumberOnly = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const shouldDisableSubmitBtn = useMemo(
    () =>
      !values.ein ||
      !values.valid ||
      !!businessEntityError ||
      appStatusError ||
      applicationStatusLoading,
    [appStatusError, applicationStatusLoading, businessEntityError, values.ein, values.valid]
  );

  const refetchAppStatusHandler = () => {
    // TODO: will move clearAllToast to APPLICATION_RECEIVED_MODAL
    clearAllToast();
    navigate(`?modal=${MODAL_NAMES.APPLICATION_RECEIVED_MODAL}`, { replace: true });
    /**
     * isOpeningModal is true when a user open a modal via URL
     * or open from Zendash dashboard e.g`?modal=enter-ein`.
     * in this case if we call `handleClose` it will redirect to `/dashboard` instead
     */
    if (!isOpeningModal) {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    const { data: patchData, error } = await patchApplicationData({
      variables: {
        input: {
          bankingApplicationId: applicationId,
          ein: values.ein
        }
      }
    });

    if (error || patchData?.status === 'Fail') {
      createCommonErrorToast({ error });
    } else {
      createSuccessToast({
        title: 'Your documents are submitted'
      });
      refetchAppStatusHandler();
    }
  };

  const isNotAllowEnterEIN = appStatus && !ALLOW_ENTER_EIN_STATUS_LIST.includes(appStatus);
  const isAllowEnterEIN = appStatus && ALLOW_ENTER_EIN_STATUS_LIST.includes(appStatus);

  useEffect(() => {
    /**
     * If application status is not null and not equal to awaiting ein
     * then close the modal
     */
    if (isNotAllowEnterEIN) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotAllowEnterEIN]);

  if (isAllowEnterEIN) {
    return (
      <>
        <DialogContent>
          <ContainerGrid>
            <FocusGrid>
              <WrapperContentStyled>
                <Stack
                  alignItems="flex-start"
                  mb="40px"
                  spacing="16px"
                >
                  <Heading
                    size="3xl"
                    weight="bold"
                  >
                    To submit your application, we need the following info.
                  </Heading>
                  <Text
                    color="tertiary"
                    size="lg"
                  >
                    Provide your EIN now to continue processing your application.
                  </Text>
                </Stack>
                <Box>
                  <Text size="xl">
                    {loading ? <Skeleton /> : `EIN for ${businessEntity?.name}`}
                  </Text>
                  <FormControl
                    fullWidth
                    sx={{ paddingTop: '24px' }}
                    variant="outlined"
                  >
                    <TextField
                      error={isShowError}
                      id="ein"
                      inputProps={{
                        maxLength: 9,
                        inputMode: 'numeric',
                        autoComplete: 'new-password'
                      }}
                      label="Employer Identification Number"
                      onBlur={() => setValues({ ...values, isDirty: true, isFocus: false })}
                      onChange={handleEinChange}
                      onFocus={() => setValues({ ...values, isFocus: true })}
                      onKeyPress={inputNumberOnly}
                      // Use text input instead of number input to prevent the display up/down arrows
                      type="text"
                      value={values.ein}
                    />
                  </FormControl>
                  {isShowError && (
                    <Text
                      color="red"
                      pt="8px"
                      size="xs"
                    >
                      {EIN_INVALID_MESSAGE}
                    </Text>
                  )}
                </Box>
              </WrapperContentStyled>
            </FocusGrid>
          </ContainerGrid>
        </DialogContent>
        <ActionButtonsGrid>
          <LoadingButton
            color="primary"
            disabled={shouldDisableSubmitBtn}
            fullWidth
            id={submitButtonId}
            loading={patchApplicationLoading || applicationStatusLoading}
            onClick={handleSubmit}
            variant="contained"
          >
            Submit
          </LoadingButton>
        </ActionButtonsGrid>
      </>
    );
  }

  return null;
}

EnterEINNumber.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default EnterEINNumber;
